import type BackOfficeConnector from "data/backOfficeConnector";
import RepositoryBase from "./repositoryBase";
import type EventBus from "../services/eventBus";
import InternalReportResponse from "entities/internalReportResponse";
import type { AxiosPromise } from "axios";
import CreateInternalReportResponse from "entities/createInternalReportResponse";
import GetInternalReportDetailResponse from "entities/getInternalReportDetailResponse";
import { excludeEmptyValues, formatDateValues, serializeFilters } from "utils/filters";
import type InternalReportRequest from "entities/internalReportRequest";
import type IncludeDeletedPaginatedRequest from "entities/includeDeletedPaginatedRequest";

export default class InternalReportsRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  postSearchReports(filters: any, queryParams?: IncludeDeletedPaginatedRequest) {
    const filter = formatDateValues(excludeEmptyValues(filters));
    return this.callApi(api =>
      api
        .all("internal_reports_search")
        .postPagedEntities<InternalReportResponse>({ ...filter, ...queryParams }, InternalReportResponse)
    );
  }
  get(id: string) {
    return this.callApi(api => api.one("internal_reports", id).getEntity(GetInternalReportDetailResponse));
  }

  create() {
    const filters = {};

    return this.callApi(api =>
      api.all("internal_reports_search").postEntities({ ...serializeFilters(filters) }, InternalReportResponse)
    );
  }

  postMobileControlReports() {
    const filters = {
      type: "ROADSIDE_CHECKS",
    };

    return this.callApi(api =>
      api.all("internal_reports_search").postEntities({ ...serializeFilters(filters) }, InternalReportResponse)
    );
  }

  postStaticControlReports() {
    const filters = {
      type: "ROAD_TOLL_PAYMENT_STATISTICS",
    };

    return this.callApi(api =>
      api.all("internal_reports_search").postEntities({ ...serializeFilters(filters) }, InternalReportResponse)
    );
  }

  postExpenseReports(queryParams: IncludeDeletedPaginatedRequest) {
    const filters = {
      type: "EXPENSES",
    };

    return this.postSearchReports(filters, queryParams);
  }

  postExemptionsReports(queryParams: IncludeDeletedPaginatedRequest) {
    const filters = {
      type: "EXEMPTIONS",
    };

    return this.postSearchReports(filters, queryParams);
  }

  postExemptionsSummaryReports(queryParams: IncludeDeletedPaginatedRequest) {
    const filters = {
      type: "EXEMPTIONS_SUMMARY",
    };

    return this.postSearchReports(filters, queryParams);
  }

  postRevenueReports(queryParams: IncludeDeletedPaginatedRequest) {
    const filters = {
      type: "REVENUES",
    };

    return this.postSearchReports(filters, queryParams);
  }

  postBusinessReports(queryParams: any) {
    const filters = {
      ...queryParams,
      type: "BUSINESS_REPORT",
    };

    return this.postSearchReports(filters);
  }

  generateNewMobileControlReport(fromDate?: Date, toDate?: Date) {
    return this.callApi(api =>
      api
        .all("internal_reports")
        .one("roadside_checks")
        .postEntity(
          {
            reportDateRange: {
              from: fromDate,
              to: toDate,
            },
          },
          CreateInternalReportResponse
        )
    );
  }

  generateNewStaticControlReport(fromDate?: Date, toDate?: Date, gatewayId?: string) {
    return this.callApi(api =>
      api
        .all("internal_reports")
        .one("road_toll_payment_statistics")
        .postEntity(
          {
            reportDateRange: {
              from: fromDate,
              to: toDate,
            },
            gatewayId: gatewayId,
          },
          CreateInternalReportResponse
        )
    );
  }

  generateNewReport(request: InternalReportRequest, name: string) {
    const requestObj = {
      fromYear: request.fromYear,
      fromMonth: request.fromMonth,
      toYear: request.toYear,
      toMonth: request.toMonth,
    };
    return this.callApi(api =>
      api.all("internal_reports").one(name).postEntity(
        {
          reportMonthRange: requestObj,
        },
        CreateInternalReportResponse
      )
    );
  }

  downloadReport(id: string): AxiosPromise {
    return this.callApi(api => api.one("internal_reports", id).one("report").getRaw({ flow: "download" }) as any);
  }
}
